import 'styled-components';

import type { ReactElement, ReactNode } from 'react';

import type { Props as BaseDialogProps } from 'components/ui/dialogs/Dialog';
import type { ButtonVariant } from 'components/ui/shared/Buttons/Button.styles';

import Dialog from 'components/ui/dialogs/Dialog';
import { OptionsContainer } from 'components/ui/dialogs/PromptDialogOptions';
import { DialogBodyContentContainer, DialogContentContainer, DialogHeader } from 'components/ui/layouts/DialogLayout';
import Button from 'components/ui/shared/Buttons/Button';
import { ElementTestId } from 'enums/testing';
import { translate } from 'utils/intlUtils';

export interface StructuredDialogButton {
  /**
   * The text on this button
   */
  label: string;
  /**
   * What happens when this button is clicked
   */
  onClick: () => void;
  /**
   * A testing ID to apply to this button
   */
  testId?: string;
  /**
   * The button variant: 'primary', 'secondary', 'destructive', or 'neutral'
   */
  variant?: ButtonVariant;
}

interface StructuredDialogProps extends Omit<BaseDialogProps, 'children' | 'isDismissible' | 'isOpen' | 'onCancel'> {
  buttons?: StructuredDialogButton[];
  children: ReactNode;
  dialogCss?: string;
  header?: string;
  icon?: ReactElement<any>;
  /**
   * Whether or not the dialog should close when you click outside of it. True by default.
   */
  isDismissible?: boolean;
  isOpen: boolean;
  onClose: () => void;
  testId?: string;
}

/**
 * A simple dialog containing one icon, some contents, and some buttons button.
 * If buttons are undefined, a default one with the text "Got it" that calls onClose when clicked will be used instead.
 * To have /no/ buttons, set it to an empty array.
 *
 * The structure is as follows:
 *   Header
 *   Icon
 *   Contents
 *   Buttons
 *
 * Everything aside from the buttons are options.
 */
export const StructuredDialog = ({
  buttons,
  children,
  dialogCss,
  header,
  icon,
  isDismissible = true,
  isOpen,
  onClose,
  testId,
  ...props
}: StructuredDialogProps) => {
  const dialogButtons = buttons || [
    {
      label: translate.t('okay'),
      onClick: onClose,
      testId: ElementTestId.STRUCTURED_DIALOG_CLOSE_BUTTON,
      variant: 'primary' as const,
    },
  ];

  return (
    <Dialog isDismissible={isDismissible} isOpen={isOpen} onCancel={onClose} {...props}>
      <DialogContentContainer css={dialogCss} data-testid={testId || ElementTestId.STRUCTURED_DIALOG_CONTAINER}>
        {header ? <DialogHeader onClose={dialogButtons.length === 1 ? onClose : undefined} title={header} /> : null}
        <DialogBodyContentContainer>
          <OptionsContainer>
            {icon}
            {children}
            {dialogButtons.length > 0 && (
              <div>
                {dialogButtons.map(button => (
                  <Button
                    data-testid={button.testId}
                    fullWidth
                    key={button.label}
                    onClick={button.onClick}
                    variant={button.variant ?? 'secondary'}
                  >
                    {button.label}
                  </Button>
                ))}
              </div>
            )}
          </OptionsContainer>
        </DialogBodyContentContainer>
      </DialogContentContainer>
    </Dialog>
  );
};
